import { Trash2 } from 'lucide-react';
import { MAX_REQUEST_NOTE_LENGTH } from '@hotel-voice/shared';
import { QuantityStepper } from '@/components/common/QuantityStepper';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency } from '@/lib/money';
import type { CartLine } from './cart';

interface OrderSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lines: CartLine[];
  total: number;
  note: string;
  onNoteChange: (note: string) => void;
  onSetQuantity: (itemId: string, quantity: number) => void;
  onRemove: (itemId: string) => void;
  onPlaceOrder: () => void;
  submitting: boolean;
  noteLabel?: string;
  notePlaceholder?: string;
}

/**
 * The receipt before it is one: every line with quantity, unit price and
 * total, the order total, and the note for the chef. The guest can still
 * change quantities here; the sheet closes itself when the last line goes.
 */
export function OrderSheet({
  open,
  onOpenChange,
  lines,
  total,
  note,
  onNoteChange,
  onSetQuantity,
  onRemove,
  onPlaceOrder,
  submitting,
  noteLabel = 'Comments for the chef',
  notePlaceholder = 'e.g. Less spicy, please.',
}: OrderSheetProps) {
  return (
    <Sheet open={open && lines.length > 0} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="gap-0">
        <SheetHeader>
          <SheetTitle>Review your order</SheetTitle>
          <SheetDescription>Check the items and total before placing the order.</SheetDescription>
        </SheetHeader>

        <div className="overflow-y-auto px-4">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-xs">
              <tr className="border-b">
                <th scope="col" className="py-2 text-left font-medium">
                  Item
                </th>
                <th scope="col" className="py-2 text-center font-medium">
                  Qty
                </th>
                <th scope="col" className="py-2 text-right font-medium">
                  Price
                </th>
                <th scope="col" className="py-2 text-right font-medium">
                  Total
                </th>
                <th scope="col" className="sr-only">
                  Remove
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {lines.map((line) => (
                <tr key={line.item.id}>
                  <td className="max-w-[9rem] py-2.5 pr-2 font-medium">
                    <span className="line-clamp-2">{line.item.name}</span>
                  </td>
                  <td className="py-2.5 text-center">
                    <QuantityStepper
                      label={line.item.name}
                      value={line.quantity}
                      onChange={(next) => onSetQuantity(line.item.id, next)}
                      disabled={submitting}
                      className="h-8"
                    />
                  </td>
                  <td className="text-muted-foreground py-2.5 text-right tabular-nums">
                    {formatCurrency(line.item.price)}
                  </td>
                  <td className="py-2.5 text-right font-medium tabular-nums">
                    {formatCurrency(line.lineTotal)}
                  </td>
                  <td className="py-2.5 pl-1 text-right">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${line.item.name}`}
                      onClick={() => onRemove(line.item.id)}
                      disabled={submitting}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Separator className="my-2" />
          <div className="flex items-center justify-between py-1 text-base font-semibold">
            <span>Total</span>
            <span className="tabular-nums">{formatCurrency(total)}</span>
          </div>
          <p className="text-muted-foreground text-xs">Charged to your room bill.</p>

          <div className="mt-4 flex flex-col gap-2">
            <Label htmlFor="chef-note">{noteLabel}</Label>
            <Textarea
              id="chef-note"
              placeholder={notePlaceholder}
              value={note}
              maxLength={MAX_REQUEST_NOTE_LENGTH}
              onChange={(event) => onNoteChange(event.target.value)}
              disabled={submitting}
              className="min-h-20"
            />
          </div>
        </div>

        <SheetFooter>
          <Button size="lg" onClick={onPlaceOrder} disabled={submitting || lines.length === 0} aria-busy={submitting}>
            {submitting ? 'Placing order…' : `Place Order · ${formatCurrency(total)}`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
